import { Button, Spin, Tooltip } from 'antd';
import { CloseOutlined, FileImageOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

interface Props {
  ids: string[];
  onRemove: (id: string) => void;
}

/**
 * 挑選關聯新聞時，已選的清單。
 * 文章有一萬四千多篇、又要換頁與換篩選條件，只靠表格裡的勾勾根本看不出自己選了什麼，
 * 所以在表格下方固定列出來（可個別移除）。id 可能來自別頁，需另外查回標題。
 *
 * 高度**固定**（不隨挑選數量變動）：上方表格的可視高度是用 `calc(100vh - …)` 扣出來的，
 * 這一區若會長高，分頁列就會疊到它上面。項目多時改由本區自己捲動。
 */
export default function SelectedRelated({ ids, onRemove }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['related', ids],
    queryFn: () => api.articlesByIds(ids),
    enabled: ids.length > 0,
  });
  const byId = new Map((data?.items ?? []).map((a) => [a.id, a]));

  return (
    <div className="mt-3 rounded-lg border border-black/5 bg-white p-3">
      <div className="mb-2 text-sm font-medium">
        已選 {ids.length} 篇
        {ids.length > 0 && <span className="ml-2 text-xs text-gray-400">（點 ✕ 可移除）</span>}
      </div>

      {ids.length === 0 ? (
        <div className="flex h-[4.75rem] items-center justify-center text-sm text-gray-400">
          尚未挑選，請勾選上方文章
        </div>
      ) : isLoading && !data ? (
        <Spin className="block h-[4.75rem] py-6 text-center" />
      ) : (
        <ul className="m-0 flex h-[4.75rem] list-none flex-wrap gap-2 overflow-y-auto p-0">
          {ids.map((id) => {
            const a = byId.get(id);
            return (
              <li
                key={id}
                className="flex min-w-0 max-w-full items-center gap-2 rounded-md bg-[#fbf3e8] py-1 pl-1 pr-1.5 md:max-w-[22rem]"
              >
                {a?.coverUrl ? (
                  <img src={a.coverUrl} alt="" className="h-8 w-12 shrink-0 rounded object-cover" />
                ) : (
                  <span className="flex h-8 w-12 shrink-0 items-center justify-center rounded bg-white text-gray-300">
                    <FileImageOutlined />
                  </span>
                )}
                <span className="min-w-0 truncate text-xs text-[#54595f]" title={a?.title}>
                  {a?.title ?? id}
                </span>
                <Tooltip title="移除">
                  <Button
                    type="text"
                    size="small"
                    aria-label={`移除 ${a?.title ?? id}`}
                    icon={<CloseOutlined />}
                    onClick={() => onRemove(id)}
                  />
                </Tooltip>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
